import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { convertFileToBase64, EMPTY_ARRAY_LENGTH } from '@modeso/aumico-lib-core-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { distinctUntilChanged } from 'rxjs/operators';
import { GetOrganizationDto } from '../../../dtos/getOrganizationDto.interface';
import { ILogo } from '../../../interfaces/logo.interface';
import { IOrganizationGeneralInformation } from '../../../interfaces/organization-general-information.interface';
import { BaseComponent } from '../../base.component';
const debug = Debug('aumico:aumico-lib-users-fe:EditUserOrganizationProfileGeneralInformationComponent');
@UntilDestroy()
@Component({
    selector: 'aumico-users-edit-organization-profile-general-information-component',
    templateUrl: 'edit-user-organization-general-information.component.html',
    styleUrls: ['edit-user-organization-general-information.component.scss'],
})
export class EditUserOrganizationGeneralInformationComponent extends BaseComponent implements OnInit {
    @Input() set userOrganization(value: GetOrganizationDto | undefined) {
        if (value != null) {
            this.organizationProfileDto = cloneDeep<GetOrganizationDto>(value);
            this.organizationProfileToSave = this.mapGetOrganizationDtoToIOrganizationGeneralInformation(value);
            this.logo = value.logo;
        }
        this.initFormGroup();
    }

    countryOptionsList: INameId[] = [];
    @Input() set countryOptions(value: INameId[]) {
        this.countryOptionsList = value;
        if (this.organizationProfileToSave != null) {
            this.updateSelectedCountry();
        }
    }

    @Output() userOrganizationProfileGeneralInformationChange = new EventEmitter<GetOrganizationDto>();

    organizationProfileFormGroup: UntypedFormGroup;
    private organizationProfileToSave: IOrganizationGeneralInformation | undefined;
    private organizationProfileDto: GetOrganizationDto | undefined;
    readonly accept = '.pdf';

    logo: ILogo | null = null;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
    ) {
        super();
        this.addSvgIcon('aumico-users-icon-edit-image');
        this.constructOrganizationProfileFormGroup();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.organizationProfileFormGroup.valueChanges
                .pipe(
                    distinctUntilChanged((previousFormValue, currentFormValue) =>
                        fastDeepEqual(previousFormValue, currentFormValue),
                    ),
                )
                .subscribe((organizationProfile: GetOrganizationDto) => {
                    const mappedOrganization =
                        this.mapIOrganizationGeneralInformationToGetOrganizationDto(organizationProfile);
                    this.userOrganizationProfileGeneralInformationChange.emit(mappedOrganization);
                }),
        );
    }

    private constructOrganizationProfileFormGroup() {
        this.organizationProfileFormGroup = this.formBuilder.group({
            name: new UntypedFormControl(null, []),
            street: new UntypedFormControl(null, []),
            streetNumber: new UntypedFormControl(null, []),
            zip: new UntypedFormControl(null, []),
            city: new UntypedFormControl(null, []),
            country: new UntypedFormControl(null, []),
            additionToAddress: new UntypedFormControl(null, []),
        });
    }

    private initFormGroup() {
        this.organizationProfileFormGroup.setValue({
            name: this.organizationProfileToSave?.name || null,
            street: this.organizationProfileToSave?.street || null,
            streetNumber: this.organizationProfileToSave?.streetNumber || null,
            zip: this.organizationProfileToSave?.zip || null,
            city: this.organizationProfileToSave?.city || null,
            country: this.organizationProfileToSave?.country || null,
            additionToAddress: this.organizationProfileToSave?.additionToAddress || null,
        });
    }

    updateOrganizationProfilePicture() {
        debug('updateOrganizationProfilePicture executed');
    }

    async onOrganizationLogoFileChange(file: File) {
        if (this.organizationProfileToSave == null) {
            throw new Error('organizationProfileToSave is null');
        }
        if (file != null) {
            const logoPDFFileBase64: string = await convertFileToBase64(file);
            if (logoPDFFileBase64 != null) {
                const logoPDFFileBase64Parts = logoPDFFileBase64.split(';base64,');
                this.logo = {
                    data: logoPDFFileBase64Parts[1],
                    name: file.name,
                };
            }
        } else {
            this.logo = null;
        }

        const mappedOrganization = this.mapIOrganizationGeneralInformationToGetOrganizationDto(
            this.organizationProfileToSave,
        );
        this.userOrganizationProfileGeneralInformationChange.emit(mappedOrganization);
    }

    private mapGetOrganizationDtoToIOrganizationGeneralInformation(
        organization: GetOrganizationDto,
    ): IOrganizationGeneralInformation {
        if (this.countryOptionsList == null || this.countryOptionsList.length === EMPTY_ARRAY_LENGTH) {
            return organization as IOrganizationGeneralInformation;
        }

        const savedCountryOption = this.countryOptionsList.find(
            (country) => country.id === organization.countryISO31661Alpha2Code,
        );
        return {
            ...organization,
            country: savedCountryOption,
        };
    }

    private updateSelectedCountry(): void {
        const savedCountryOption = this.countryOptionsList.find(
            (country) => country.id === this.organizationProfileDto.countryISO31661Alpha2Code,
        );

        this.organizationProfileToSave = {
            ...this.organizationProfileDto,
            country: savedCountryOption,
        };

        this.organizationProfileFormGroup.get('country').setValue(savedCountryOption);
    }

    private mapIOrganizationGeneralInformationToGetOrganizationDto(
        formValue: IOrganizationGeneralInformation,
    ): GetOrganizationDto {
        // replacing empty white space with null
        const filteredValue: IOrganizationGeneralInformation = JSON.parse(JSON.stringify(formValue), (key, value) => {
            return value === '' ? null : value;
        });
        return {
            name: filteredValue.name,
            street: filteredValue.street,
            streetNumber: filteredValue.streetNumber,
            additionToAddress: filteredValue.additionToAddress,
            zip: filteredValue.zip,
            city: filteredValue.city,
            logo: this.logo,
            countryISO31661Alpha2Code: filteredValue.country.id,
        } as GetOrganizationDto;
    }

    private addSvgIcon(name: string) {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }
}
